import React, { useState, useEffect } from 'react';
import { Layout, Menu, Badge, Avatar, Typography, Space, Divider } from 'antd';
import {
  UserOutlined,
  ShoppingCartOutlined,
  StockOutlined,
  BarChartOutlined,
  TransactionOutlined,
  SettingOutlined,
  PercentageOutlined,
  LogoutOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
  MenuFoldOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const { Sider } = Layout;
const { Text, Title } = Typography;

const Sidebar = ({ mobileMenuOpen, onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [collapsed, setCollapsed] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(false); // Always expanded on mobile when open
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    {
      key: '/users',
      icon: <UserOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          👥 Users
        </span>
      ),
    },
    {
      key: '/orders',
      icon: <ShoppingCartOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          📊 Orders
        </span>
      ),
    },
    {
      key: '/transactions',
      icon: <TransactionOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          💳 Transactions
        </span>
      ),
    },
    {
      key: '/deposit-approvals',
      icon: <CheckCircleOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          💰 Deposit Approvals
        </span>
      ),
    },
    {
      key: '/withdrawal-approvals',
      icon: <TransactionOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          💸 Withdrawal Approvals
        </span>
      ),
    },
    {
      key: '/margin-settings',
      icon: <PercentageOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          💰 Margin Settings
        </span>
      ),
    },
    {
      key: '/settings',
      icon: <SettingOutlined style={{ fontSize: isMobile ? '18px' : '16px' }} />,
      label: (
        <span style={{ 
          fontSize: isMobile ? '16px' : '14px', 
          fontWeight: 500,
          marginLeft: '8px'
        }}>
          ⚙️ Settings
        </span>
      ),
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else {
      navigate(key);
      // Close mobile menu after navigation
      if (window.innerWidth <= 992) {
        onMobileMenuToggle(false);
      }
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobile && mobileMenuOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99,
            backdropFilter: 'blur(4px)'
          }}
          onClick={() => onMobileMenuToggle(false)}
        />
      )}

      <Sider
        width={isMobile ? 280 : 250}
        breakpoint="lg"
        collapsedWidth="0"
        collapsed={isMobile ? !mobileMenuOpen : collapsed}
        className={mobileMenuOpen ? 'mobile-sidebar-open' : ''}
        style={{
          background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
          boxShadow: isMobile 
            ? '4px 0 20px rgba(0,0,0,0.3)' 
            : '2px 0 8px rgba(0,0,0,0.1)',
          position: 'fixed',
          height: '100vh',
          left: isMobile && !mobileMenuOpen ? '-280px' : 0,
          top: 0,
          zIndex: 100,
          transition: 'left 0.3s ease-in-out',
          borderRadius: isMobile ? '0 20px 20px 0' : '0'
        }}
      >
        {/* Enhanced Header */}
        <div style={{
          padding: isMobile ? '32px 20px 24px' : '24px 16px',
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
          marginBottom: '20px',
          position: 'relative'
        }}>
          {/* Mobile Close Button */}
          {isMobile && (
            <button
              onClick={() => onMobileMenuToggle(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <CloseOutlined style={{ fontSize: '16px' }} />
            </button>
          )}

          {/* Logo/Avatar */}
          <div style={{
            background: 'rgb(255, 255, 255)',
            borderRadius: '16px',
            width: isMobile ? '64px' : '56px',
            height: isMobile ? '64px' : '56px',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
            padding: '8px'
          }}>
            <img 
              src={require('../styles/logo.png')} 
              alt="Trading Admin Logo"
              style={{ 
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: '8px'
              }} 
            />
          </div>

          <Title level={3} style={{
            color: 'white',
            margin: '0 0 8px 0',
            fontSize: isMobile ? '22px' : '18px',
            fontWeight: 700,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}>
            Trading Admin
          </Title>
          <Text style={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: isMobile ? '14px' : '12px',
            fontWeight: 500
          }}>
            Management Dashboard
          </Text>
        </div>

        {/* Navigation Menu */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: '0 12px'
        }}>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={handleMenuClick}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: isMobile ? '16px' : '14px'
            }}
            theme="dark"
            className="custom-sidebar-menu"
          />
        </div>

        {/* Enhanced Logout Section */}
        <div style={{
          padding: isMobile ? '20px 16px 24px' : '16px',
          borderTop: '1px solid rgba(255,255,255,0.15)',
          background: 'rgba(255,255,255,0.05)'
        }}>
          <Menu
            mode="inline"
            items={[
              {
                key: 'logout',
                icon: <LogoutOutlined style={{ 
                  fontSize: isMobile ? '18px' : '16px',
                  color: '#ff4d4f'
                }} />,
                label: (
                  <span style={{ 
                    fontSize: isMobile ? '16px' : '14px', 
                    fontWeight: 500,
                    marginLeft: '8px',
                    color: '#ff4d4f'
                  }}>
                    🚪 Logout
                  </span>
                ),
                style: {
                  background: 'rgba(255, 77, 79, 0.1)',
                  borderRadius: '8px',
                  margin: '4px 0',
                  border: '1px solid rgba(255, 77, 79, 0.2)'
                }
              },
            ]}
            onClick={handleMenuClick}
            style={{
              background: 'transparent',
              border: 'none'
            }}
            theme="dark"
          />
        </div>
      </Sider>

      {/* Custom Styles */}
      <style jsx="true">{`
        .custom-sidebar-menu .ant-menu-item {
          height: ${isMobile ? '48px' : '40px'} !important;
          line-height: ${isMobile ? '48px' : '40px'} !important;
          margin: 4px 0 !important;
          border-radius: 8px !important;
          transition: all 0.3s ease !important;
        }
        
        .custom-sidebar-menu .ant-menu-item:hover {
          background: rgba(255,255,255,0.15) !important;
          transform: translateX(4px);
        }
        
        .custom-sidebar-menu .ant-menu-item-selected {
          background: rgba(255,255,255,0.2) !important;
          border-right: 3px solid #fff !important;
          font-weight: 600 !important;
        }
        
        .custom-sidebar-menu .ant-menu-item-icon {
          margin-right: 12px !important;
        }
      `}</style>
    </>
  );
};

export default Sidebar;